<template>
    <Loading :loading="loading" :preserve-height="true" :inset="true">
        <div class="col-span-1 lg:col-span-2 xl:col-span-3 relative">
            <h4 @click="closePopup">
                {{ $translate(productGroup.name) }}
            </h4>

            <span class="text-sm" v-html="$translate(productGroup.short_description)"></span>

            <div ref="product_list" class="w-full overflow-y-scroll overflow-x-visible no-scrollbar">
                <div class="flex flex-row items-center gap-4">
                    <div ref="overlay_right" class="overlay-right absolute z-20 right-0 h-[155px] w-[50px] pointer-events-none transition-all ease-in-out block lg:hidden" />
                    <div ref="overlay_left" class="overlay-left absolute z-20 left-0 h-[155px] w-[25px] pointer-events-none transition-all ease-in-out block lg:hidden" />

                    <div
                        v-for="(productVariant, index) in productVariantsWritten"
                        :key="productVariant.id"
                        :class="selectedProductVariant === productVariant ? 'active' : ''"
                        class="product-variant rounded-md shadow-md p-4 cursor-pointer relative my-3 whitespace-nowrap text-center transition-all ease-in-out"
                        @click="selectProduct(index)"
                    >
                        <div
                            v-if="productVariant.discount_percent > 0"
                            class="absolute -top-2 -right-2 rounded-full gradient text-white h-8 w-8 shadow-md flex items-center justify-center discount text-xxs"
                        >
                            <span>
                                {{ $toPercent(productVariant.discount_percent, 0) }}
                            </span>
                        </div>
                        <strong>
                            {{ $translate(getAttributeByType(productVariant, 'period').title) }}
                        </strong>
                        <br />
                        <small>
                            {{ $toCurrency(productVariant.base_price_gross - productVariant.base_price_gross * productVariant.discount_percent) }}<br />
                            <span v-if="!$isOriginalCurrency()" class="text-gray-400">
                                ({{ $toCurrency(productVariant.base_price_gross - productVariant.base_price_gross * productVariant.discount_percent, 2, true) }})
                            </span>
                        </small>
                        <br />
                        <span class="ml-1 text-xxs">
                            {{ $t('per_month') }}
                        </span>
                    </div>
                </div>
            </div>
            <BlackWeekBanner img-class="rounded-xl" />
            <small class="text-xxxs italic text-gray-600">
                {{ $t('payment_disclaimer') }}<br />
                * {{ $t('payment_disclaimer_addon') }}
            </small>

            <div class="col-span-1">
                <ProductWebinarOption
                    v-if="productVariantsWebinars"
                    v-model:toggle-selected-webinar="toggleSelectedWebinar"
                    :selected-webinar="selectedWebinarVariant"
                    :data="productGroup"
                    :product-category="productCategory"
                />
            </div>

            <div class="flex flex-row">
                <div class="rounded-md py-2 w-1/2 md:w-1/3 gradient my-auto cursor-pointer" @click="intoCart">
                    <p class="w-full text-white text-center text-xs">
                        {{ $t('payment_add_to_cart') }}
                    </p>
                </div>
                <div class="grow"></div>
                <p class="font-bold">
                    {{ $toCurrency(sum) }}
                </p>
            </div>
        </div>
    </Loading>
</template>

<script>
import { mapGetters } from 'vuex';
import i18next from 'i18next';
import Toastification from '../miscellaneous/Toastification.vue';
import _ from 'lodash';
import ProductWebinarOption from './ProductWebinarOption.vue';

export default {
    components: { ProductWebinarOption },
    props: {
        open: Boolean,
        productGroup: Object,
        productCategory: Object,
        preSelectedVariant: String,
        preSelectedWritten: Object,
        preSelectedWebinar: Object
    },
    emits: ['open'],
    data: () => ({
        loading: false,
        variant: null,
        selectedProductVariant: null,
        selectedWebinarVariant: null,
        productVariantsWritten: null,
        productVariantsWebinars: null,
        toggleSelectedWebinar: true,
        sum: null
    }),
    async mounted() {
        this.loading = true;
        await this.$store.dispatch('product_group/show', this.productGroup.id);

        this.productVariantsWritten = this.sortProductVariantsByFrequency(this.getProduct.productVariants.data);

        if (this.getWebinar?.productVariants) {
            this.productVariantsWebinars = this.sortProductVariantsByFrequency(this.getWebinar?.productVariants?.data);
        }

        this.selectedProductVariant = this.preSelectedWritten;

        if (this.preSelectedWebinar) {
            this.selectedWebinarVariant = this.preSelectedWebinar;
        } else {
            this.toggleSelectedWebinar = false;
        }

        this.variant = this.preSelectedVariant;
        this.calculatePrice();
        this.loading = false;

        try {
            const defVariant = this.selectedProductVariant || this.productVariantsWritten?.[0];

            if (defVariant) {
                this.pushSelectItem(this.productGroup, defVariant);
            }
        } catch (e) {
            // silent
        }
    },

    beforeUnmount() {
        this.$emit('open', false);
    },

    methods: {
        selectProduct(index) {
            this.variant = index;
            this.selectedProductVariant = this.productVariantsWritten[index];
            this.selectWebinar();
            this.calculatePrice();

            // GA4: Report selection in popup
            this.pushSelectItem(this.productGroup, this.selectedProductVariant);
        },
        getAttributeByType(productVariant, type) {
            return productVariant.productAttributeValues.data.find((productAttributeValue) => {
                return productAttributeValue.meta.type === type;
            });
        },
        selectWebinar() {
            this.toggleSelectedWebinar ? (this.selectedWebinarVariant = this.productVariantsWebinars[this.variant]) : (this.selectedWebinarVariant = null);
            this.calculatePrice();
        },
        closePopup() {
            this.$emit('open', false);
        },
        calculatePrice() {
            const productPrice = this.selectedProductVariant ? this.selectedProductVariant.total_end_price_gross : 0;
            const webinarPrice = this.selectedWebinarVariant ? this.selectedWebinarVariant.total_end_price_gross : 0;
            this.sum = productPrice + webinarPrice;
        },
        async intoCart() {
            const products = [];
            this.selectedProductVariant = this.productVariantsWritten[this.variant];
            this.selectWebinar();

            if (this.selectedProductVariant) {
                products.push(this.selectedProductVariant);
            }

            if (this.selectedWebinarVariant) {
                products.push(this.selectedWebinarVariant);
            }

            for (const selectedProductVariant of products) {
                await this.$store
                    .dispatch('cart_products/store', {
                        product_variant_id: selectedProductVariant.id
                    })
                    .then((response) => {
                        // if no message in response the product was added succesfully
                        if (!response?.meta?.message) {
                            this.$toast.success(
                                {
                                    component: Toastification,
                                    props: {
                                        message: i18next.t('succesfully_added_to_cart'),
                                        type: 'success'
                                    }
                                },
                                this.closePopup()
                            );
                        } else if (response?.meta?.message.info) {
                            this.$toast.success(
                                {
                                    component: Toastification,
                                    props: {
                                        message: response?.meta?.message.info,
                                        type: 'success'
                                    }
                                },
                                this.closePopup()
                            );
                        } else {
                            this.$toast.info({
                                component: Toastification,
                                props: {
                                    message: response?.meta?.message.danger ? response?.meta?.message.danger : i18next.t('msg_action_failed'),
                                    type: 'info'
                                }
                            });
                        }
                    });
            }

            this.$store.dispatch('cart/index');
        },
        sortProductVariantsByFrequency(productVariants) {
            productVariants = _.sortBy(productVariants, (productVariant) => {
                const productAttributeValue = productVariant.productAttributeValues.data.find((productAttributeValue) => {
                    return productAttributeValue.meta.type === 'period';
                });

                return productAttributeValue.meta.runtime_in_days;
            });

            return productVariants;
        },
        buildGa4Item(productGroup, productVariant, index = 0) {
            if (!productGroup || !productVariant) return null;

            // ID (nimm deine interne ID – falls du slugs bevorzugst, hier anpassen)
            const item_id = `${productVariant.id}`;

            // Name
            const item_name = (this.$translate && this.$translate(productGroup?.name)) || productGroup?.name?.de || productGroup?.name || 'Produkt';

            // Category – first from full group, then fallback to productCategory
            const item_category =
                this.$translate?.(productGroup?.productCategories?.data?.[0]?.name) ||
                productGroup?.productCategories?.data?.[0]?.name?.en ||
                productGroup?.productCategories?.data?.[0]?.name ||
                this.$translate?.(this.productCategory?.name) ||
                this.productCategory?.name?.en ||
                this.productCategory?.name ||
                'Category';

            // Variante (Perioden-Attribut)
            const periodAttr = this.getAttributeByType(productVariant, 'period');
            const item_variant = (this.$translate && this.$translate(periodAttr?.title)) || periodAttr?.title?.de || periodAttr?.title || 'Variante';

            // Price as in UI (monthly basis, with discount)
            const base = Number(productVariant?.base_price_gross ?? 0);
            const discountPercent = Number(productVariant?.discount_percent ?? 0);
            const price = Number((base - base * discountPercent).toFixed(2));

            const currency = (this.$getCurrencyCode && this.$getCurrencyCode()) || 'EUR';

            return {
                item_id,
                item_name,
                item_category,
                item_variant,
                price,
                currency,
                quantity: 1,
                index
            };
        },

        pushSelectItem(productGroup, productVariant, item_list_name = 'product-mini') {
            const item = this.buildGa4Item(productGroup, productVariant, 0);

            if (!item) return;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'select_item',
                ecommerce: {
                    item_list_name,
                    currency: item.currency,
                    value: item.price,
                    items: [item]
                }
            });
        }
    },
    computed: {
        background() {
            return 'linear-gradient(to bottom, ' + this.productCategory.background_color + ' 0%, ' + this.productCategory.background_gradient + ' 100%)';
        },
        ...mapGetters('product_group', {
            getProduct: 'getProduct',
            getWebinar: 'getWebinar'
        })
    },
    watch: {
        toggleSelectedWebinar() {
            this.selectWebinar();
            this.calculatePrice();
        }
    }
};
</script>

<style scoped>
.product-variant.active {
    background: v-bind(background);
    color: #ffffff;
}

.gradient {
    background: v-bind(background);
}
</style>
